// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Currency formatting and validation utilities.
 * This module provides functions for formatting currency values and Zod schemas
 * for validating dollar amounts with appropriate constraints.
 */
import { z } from 'zod';

/**
 * Maximum dollar value allowed in the system.
 * This limit is aligned with the database NUMERIC(14,2) precision, which supports
 * values up to $999,999,999,999.99 (12 digits before the decimal point).
 * All monetary columns in the database use this unified precision for consistency.
 */
export const MAX_DOLLAR_VALUE = 999999999999.99;

/**
 * Pre-formatted dollar cap ("$999,999,999,999.99") for use in user-facing
 * messages. Single source of truth so the cap formatting can't drift
 * across screens.
 */
export const MAX_DOLLAR_VALUE_LABEL = `$${MAX_DOLLAR_VALUE.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const boundMessage = `Dollar value must not exceed ${MAX_DOLLAR_VALUE_LABEL}`;

function isWithinDollarBound(value: string): boolean {
  const asNumber = Number(value);
  return !Number.isNaN(asNumber) && asNumber <= MAX_DOLLAR_VALUE;
}

/**
 * Required dollar string. Empty input fails with "This field is required".
 */
export const dollarStringSchema = z
  .string({
    required_error: 'This field is required',
    invalid_type_error: 'This field is required',
  })
  .min(1, 'This field is required')
  .refine(isWithinDollarBound, { message: boundMessage });
